import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useDevAuth } from '../utils/useDevAuth';
import OrgUsagePanel from '../components/OrgUsagePanel';

interface OrgOption {
  id: number;
  name: string;
}

/**
 * Admin Dashboard Logs - site usage across all organizations.
 *
 * Same panel as the org detail page's Usage tab, with an org selector on top.
 * "All organizations" is the default and is the only view that includes events
 * from routes which aren't org-scoped (most admin pages).
 */
const AdminDashboardLogs: React.FC = () => {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const { user } = useDevAuth();

  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<number | ''>('');

  const provider = user?.sub?.split('|')[0];
  const emailLower = user?.email?.toLowerCase() || '';
  const isAdmin = (provider === 'google-oauth2' || provider === 'clerk') && (emailLower === 'weezilla@gmail.com' || emailLower.endsWith('@crant.ai'));

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get('/api/admin/organizations');
        if (cancelled) return;
        // The endpoint returns { organizations: [...] }, not a bare array.
        const raw = Array.isArray(res.data) ? res.data : (res.data?.organizations || []);
        // A handful of rows (ids 1-4) have a null organization_name, which makes
        // a bare localeCompare throw and would drop the whole list.
        const list: OrgOption[] = raw
          .map((o: any) => ({
            id: o.id ?? o.organizationId,
            name: o.name ?? o.organizationName ?? `(unnamed org ${o.id ?? o.organizationId})`,
          }))
          .filter((o: OrgOption) => o.id != null)
          .sort((a: OrgOption, b: OrgOption) => String(a.name).localeCompare(String(b.name)));
        setOrgs(list);
      } catch (e) {
        // Non-fatal: the panel still works, just without the org filter. Logged
        // rather than swallowed -- an empty dropdown is otherwise a silent
        // mystery.
        console.error('Failed to load organization list for the usage filter', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, axiosClient]);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Unauthorized</Typography>
        <Typography>You do not have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Button variant="text" onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
        &larr; Back to Admin Dashboard
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Dashboard Logs
        </Typography>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            label="Organization"
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">All organizations</MenuItem>
            {orgs.map(o => (
              <MenuItem key={o.id} value={o.id}>{o.name} ({o.id})</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <OrgUsagePanel
        organizationId={selectedOrg === '' ? null : selectedOrg}
        title={selectedOrg === '' ? 'All organizations' : (orgs.find(o => o.id === selectedOrg)?.name || 'Usage')}
      />
    </Box>
  );
};

export default AdminDashboardLogs;
